import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  EXCLUDED_FIELD_SETTING_ID,
  SettingRes,
  YearClosureTableData,
  YearEndProcedureSettingsReq,
  YearEndProcedureSettingsRes
} from '../types';

import { createSchema } from '../utils/createSchema';

import { LoadingButton } from '@mui/lab';

import { ControlledCheckbox } from 'components/shared';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import useSaveSettings from '../hooks/useSaveSettings';

interface Props {
  rowData: YearClosureTableData | null;
  settings?: YearEndProcedureSettingsRes | undefined;
  handleClose: () => void;
  startYearClosure: () => void;
}

const convertSettingsToRequest = (
  formData,
  settings
): YearEndProcedureSettingsReq => {
  return settings.map((item) => ({ ...item, value: formData[item.settingId] }));
};

const convertSettingsToSchema = (
  settings: SettingRes[] | undefined
): Record<string, boolean> => {
  const settingsObj = {};
  settings?.forEach((settings) => {
    settingsObj[settings.settingId] = Boolean(settings.value);
  });
  return settingsObj;
};

export const CloseYearFormComponent = ({
  rowData,
  settings,
  startYearClosure,
  handleClose
}: Props) => {
  const { t } = useTranslation('common');
  const { isLoading, mutate: saveSettings } = useSaveSettings(
    handleClose,
    startYearClosure
  );

  const schema = createSchema(settings);

  const methods = useForm({
    defaultValues: schema.cast({
      settings: convertSettingsToSchema(settings?.settings)
    }),
    resolver: yupResolver(schema)
  });
  const { control, handleSubmit } = methods;

  const onSubmit = (data: Record<number, boolean>) => {
    const request = {
      ...settings,
      settings: convertSettingsToRequest(data, settings?.settings)
    };
    saveSettings(request);
  };

  return (
    <MultiStepForm methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <MultiStepForm.Section title={t('year')}>
        {rowData?.year}
      </MultiStepForm.Section>
      <MultiStepForm.Section title={t('yearClosure.settings')}>
        {settings?.settings
          .filter((item) => item.settingId !== EXCLUDED_FIELD_SETTING_ID)
          .map((item) => (
            <ControlledCheckbox
              key={item.key}
              control={control}
              name={item.settingId.toString()}
              label={t(
                `yearClosure.yearEndSettings.${item.key.replaceAll('.', '')}`
              )}
            />
          ))}
      </MultiStepForm.Section>
      <MultiStepForm.Actions>
        <LoadingButton
          loading={isLoading}
          type="submit"
          variant="contained"
          autoFocus
          color="primary"
        >
          {t('save')}
        </LoadingButton>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};
